import React, { useEffect, useState } from "react";
import FloatingLabelDropDown from "../../../../../common-components/floatingdropdown";
import SweetAlert from "react-bootstrap-sweetalert";
import {
  IEmailConfiguration,
  IEmailConfigurationEdit,
} from "../../../../../models/master/email";
import {
  cloneDeep,
  emailRegex,
} from "../../../../../constants/commonConstants";
import {
  alertConstants,
  getAlertData,
} from "../../../../../helper/sweet-alert";
import Joi from "joi";
import { validateFields } from "../../../../../helper/joi-validation";
import { connect, useDispatch } from "react-redux";
import { setEmailConfiguration } from "../../../../../store/slices/master/emailConfigurationSlice";
import FloatingLabelTextInput from "../../../../../common-components/floatingtextinput";
import {
  getEmailBlocks,
  getEmailBlockUserTypes,
  updateEmailConfiguration,
} from "../../../../../action/masters/emailConfigurationActionCreator";
import {
  getColleges,
  getUserPageBlocks,
} from "../../../../../action/common/commonActionCreator";
import MultipleSelect from "../../../../../common-components/multipleSelect";
import LoaderSpinner from "../../../../../common-components/loader";
import { sortAlphabetically } from "../../../../../common-functions/commonFunctions";
import { setUserTypes } from "../../../../../store/slices/common/commonSlice";
import { areObjectsEqual } from "../../../../../helper/generic-helpers";

const EmailConfigurationEdit = (props: IEmailConfigurationEdit) => {
  const dispatch = useDispatch();
  const {
    setShowEdit,
    updateEmailConfigurations,
    getEmailConfigurations,
    emailConfiguration,
    getColleges,
    colleges,
    userTypes,
    getEmailBlockUserTypes,
    getEmailBlocks,
    emailBlocks,
  } = props;

  const [data, setData] = useState<IEmailConfiguration>(
    cloneDeep(emailConfiguration)
  );
  const [initialData, setInitialData] = useState<
    IEmailConfiguration | undefined
  >(undefined);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<any>("success");
  const [alertTitle, setAlertTitle] = useState<string>("Default");
  const [errors, setErrors] = useState<any>(undefined);
  const [showAlertCancel, setShowAlertCancel] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasChange, setHasChange] = useState(false);

  const onChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    errors && delete errors[event.target.name];
    const _data = { ...data, [event.target.name]: event.target.value };
    if (event.target.name === "emailBlockId") {
      getEmailBlockUserTypes(Number(event.target.value));
      _data.userType = [];
    }
    setData(_data);
  };

  useEffect(() => {
    if (emailConfiguration) {
      let _emailConfiguration = cloneDeep(emailConfiguration);
      setData(_emailConfiguration);
      setInitialData(_emailConfiguration);
      getEmailBlockUserTypes(_emailConfiguration.emailBlockId);
      getUserPageBlocks(Number(_emailConfiguration.userType));
    }
  }, [emailConfiguration]);

  // setting sweet alert dynamically
  const setAlert = (type: string) => {
    let alertData = getAlertData(type);
    if (alertData) {
      setShowAlert(true);
      setAlertType(alertData.type);
      setAlertTitle(alertData.title);
    }
  };

  // executed on clicking 'OK' in sweet alert.
  const onConfirm = () => {
    if (alertType === alertConstants.error) {
      setShowAlert(false);
    } else {
      getEmailConfigurations();
      setShowEdit(false);
    }
  };

  const schema = Joi.object().keys({
    userType: Joi.array()
      .items(Joi.number().greater(0))
      .min(1)
      .required()
      .label("Select User Type"),
    emailBlockId: Joi.number().greater(0).required().label("Select Page Block"),
    collegeId: Joi.number().greater(0).required().label("Select University Management"),
    toMailId: Joi.string()
      .required()
      .regex(emailRegex)
      .label("Enter Valid Email"),
  });

  const validationData = {
    ...data,
  };

  const onUpdate = async () => {
    const validationErrors = validateFields(validationData, schema);
    if (validationErrors === undefined) {
      setLoading(true);
      try {
        await updateEmailConfigurations(data, data.id ?? 0, setAlert);
      } finally {
        setLoading(false);
      }
    } else {
      setErrors(validationErrors);
    }
  };

  useEffect(() => {
    if (data && initialData) {
      setHasChange(!areObjectsEqual(data, initialData));
    }
  }, [data, initialData]);

  useEffect(() => {
    getColleges();
    getEmailBlocks();
    return () => {
      dispatch(setEmailConfiguration(undefined));
      dispatch(setUserTypes([]));
    };
  }, []);

  return (
    <>
      {data && (
        <>
          <div className="modal-header">
            <h1 className="modal-title fs-5">Edit Email Configuration</h1>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowEdit(false)}
            ></button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-lg-12 form-group">
                <FloatingLabelDropDown
                  label={"Select University Management "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data?.collegeId}
                  name={"collegeId"}
                  optionsList={sortAlphabetically(colleges)}
                />
                {errors?.collegeId && (
                  <div className="text-danger">{errors.collegeId}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <FloatingLabelDropDown
                  label={"Select Block "}
                  onChange={onChange}
                  isMandatory={true}
                  value={data?.emailBlockId}
                  name={"emailBlockId"}
                  optionsList={sortAlphabetically(emailBlocks)}
                />
                {errors?.emailBlockId && (
                  <div className="text-danger">{errors.emailBlockId}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <MultipleSelect
                  label={"User Type "}
                  onChange={onChange}
                  isMandatory={true}
                  values={data?.userType as number[]}
                  name={"userType"}
                  optionList={sortAlphabetically(userTypes)}
                />
                {errors?.userType && (
                  <div className="text-danger">{errors.userType}</div>
                )}
              </div>
              <div className="col-lg-12 form-group">
                <FloatingLabelTextInput
                  label={"Email Id "}
                  maxLength={50}
                  onChange={onChange}
                  isMandatory={true}
                  value={data.toMailId}
                  name={"toMailId"}
                  type="email"
                />
                {errors?.toMailId && (
                  <div className="text-danger">{errors.toMailId}</div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onUpdate}
              disabled={!hasChange}
            >
              Update
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowEdit(false)}
            >
              Close
            </button>
          </div>
          <SweetAlert
            show={showAlert}
            success={false}
            type={alertType}
            title={alertTitle}
            onConfirm={() => onConfirm()}
            showCancel={showAlertCancel}
            onCancel={() => {
              setShowAlert(false);
              setShowAlertCancel(false);
            }}
            cancelBtnText={"No"}
            confirmBtnText={showAlertCancel ? "Yes" : "Ok"}
          ></SweetAlert>
          {loading && <LoaderSpinner />}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = {
  updateEmailConfigurations: (data: any, id: number, callBack: Function) =>
    updateEmailConfiguration(data, id, callBack),
  getColleges: () => getColleges(),
  getEmailBlockUserTypes: (emailBlockId?: number) =>
    getEmailBlockUserTypes(emailBlockId),
  getEmailBlocks: () => getEmailBlocks(),
};

function mapStateToProps(state: any) {
  return {
    colleges: state.common.colleges,
    emailBlocks: state.common.emailBlocks,
    userTypes: state.common.userTypes,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmailConfigurationEdit);
